// Componente de barra de búsqueda para el navbar
// Incluye autocompletado y sugerencias

import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useProductSearchWithDebounce, getProductSearchUrl } from '../../hooks/useProductSearch';

interface SearchBarProps {
  className?: string;
  placeholder?: string;
  showSuggestions?: boolean;
  maxSuggestions?: number;
}

export function SearchBar({
  className = '',
  placeholder = 'Buscar productos...',
  showSuggestions = true,
  maxSuggestions = 5,
}: SearchBarProps) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  // Hook de búsqueda con debounce
  const { data: searchData, isLoading, error } = useProductSearchWithDebounce(
    query,
    300 // 300ms de debounce
  );

  // Manejar cambios en el input
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setQuery(value);
    setIsOpen(value.length >= 2);
    setSelectedIndex(-1);
  };

  // Manejar envío del formulario
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (query.trim()) {
      navigate(getProductSearchUrl(query));
      setIsOpen(false);
      setQuery('');
    }
  };

  // Manejar clic en una sugerencia
  const handleSuggestionClick = (product: { slug: string; name: string; categoryId: string }) => {
    // Por ahora, navegamos directamente al producto usando el slug
    // TODO: Implementar navegación completa con catalog y category
    navigate(`/producto/${product.slug}`);
    setIsOpen(false);
    setQuery('');
  };

  // Manejar navegación con teclado
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen || !searchData?.data) return;

    const suggestions = searchData.data.slice(0, maxSuggestions);

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < suggestions.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev > 0 ? prev - 1 : suggestions.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
          handleSuggestionClick(suggestions[selectedIndex].product);
        } else if (query.trim()) {
          const searchUrl = getProductSearchUrl(query);
          navigate(searchUrl);
          setIsOpen(false);
          setQuery('');
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setSelectedIndex(-1);
        break;
    }
  };

  // Cerrar sugerencias al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Obtener sugerencias
  const suggestions = (searchData?.data && Array.isArray(searchData.data)) 
    ? searchData.data.slice(0, maxSuggestions) 
    : [];

  return (
    <div className={`relative ${className}`}>
      <form onSubmit={handleSubmit} className="relative">
        <div className="relative">
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onFocus={() => query.length >= 2 && setIsOpen(true)}
            placeholder={placeholder}
            className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          <button
            type="submit"
            className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </button>
        </div>
      </form>

      {/* Sugerencias */}
      {showSuggestions && isOpen && (
        <div
          ref={suggestionsRef}
          className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-80 overflow-y-auto"
        >
          {isLoading && (
            <div className="px-4 py-3 text-gray-500 text-center">
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500 mr-2"></div>
                Buscando...
              </div>
            </div>
          )}

          {error && (
            <div className="px-4 py-3 text-red-500 text-center">
              Error al buscar productos
            </div>
          )}

          {!isLoading && !error && suggestions.length === 0 && query.length >= 2 && (
            <div className="px-4 py-3 text-gray-500 text-center">
              No se encontraron productos
            </div>
          )}

          {!isLoading && !error && suggestions.length > 0 && (
            <div className="py-2">
              {suggestions.map((result: { product: { slug: string; name: string; categoryId: string; id: string } }, index: number) => (
                <button
                  key={result.product.id}
                  onClick={() => handleSuggestionClick(result.product)}
                  className={`w-full px-4 py-3 text-left hover:bg-gray-50 focus:outline-none focus:bg-gray-50 ${
                    index === selectedIndex ? 'bg-gray-50' : ''
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-gray-200 rounded flex items-center justify-center">
                      <svg
                        className="w-5 h-5 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                        />
                      </svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {result.product.name}
                      </p>
                      <p className="text-xs text-gray-500 truncate">
                        Producto
                      </p>
                    </div>
                    <div className="text-xs text-gray-400">
                      ★
                    </div>
                  </div>
                </button>
              ))}
              
              {query.trim() && (
                <div className="border-t border-gray-200">
                  <button
                    onClick={() => {
                      const searchUrl = getProductSearchUrl(query);
                      navigate(searchUrl);
                      setIsOpen(false);
                      setQuery('');
                    }}
                    className="w-full px-4 py-3 text-left text-blue-600 hover:bg-blue-50 focus:outline-none focus:bg-blue-50"
                  >
                    <div className="flex items-center">
                      <svg
                        className="w-4 h-4 mr-2"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                        />
                      </svg>
                      Ver todos los resultados para "{query}"
                    </div>
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
